import axios from "@/axios";
import router from "@/routes/router.js";
import Util from "@/util/util";

export const namespaced = true;

const defaultData = {
  id: null,
  name: null,
  email: null,
  roles: [],
  loginMethod: null,
};

export const state = {
  user: { ...defaultData }, 
  authenticatedUserId: null,
  status: null,
  lastLoggedInPath: null,
};

export const getters = {
  isLoggedIn: (state) =>
    !!state.user && !!state.user.name && !!state.user.email,
  authStatus: (state) => state.status,
  getUser: (state) => state.user,
  isPartnerAccount: (state) =>
    state.user && state.user.roles
      ? state.user.roles.indexOf(Util.PARTNER_NATION) > -1
      : false,
  isPM1Account: (state) =>
    state.user && state.user.roles
      ? state.user.roles.indexOf(Util.PM1) > -1
      : false,
  authUserId: (state) => state.authenticatedUserId,
  getLoginMethod: (state) => state.user.loginMethod,
  isLogistics: (state) =>
    !!state.user.roles && state.user.roles.indexOf("Logistics") > -1,
  isFinance: (state) =>
    !!state.user.roles && state.user.roles.indexOf("Finance") > -1,
  isSponsor: (state) =>
    !!state.user.roles && state.user.roles.indexOf("Sponsor") > -1,
  isSuperAdmin: (state) =>
    !!state.user.roles && state.user.roles.indexOf("AGATRS Team") > -1,
  isOUSD: (state) =>
    !!state.user.roles && state.user.roles.indexOf("Ousd") > -1,
  isViewOnly: (state) =>
    !!state.user.roles && state.user.roles.indexOf("ViewOnly") > -1,
  isPNManager: (state) =>
    !!state.user.roles && state.user.roles.indexOf("PartnerNationManager") > -1,
  isTier1: (state) =>
    !!state.user.roles && state.user.roles.indexOf("Tier1"), // Fixed typo indexof to indexOf
  // New getter for last logged-in path
  getLastLoggedInPath: (state) => state.lastLoggedInPath,
};

export const mutations = {
  AUTH_REQUEST(state) {
    state.status = "loading";
  },
  AUTH_LOGIN_SUCCESS(state) {
    state.status = "success";
  },
  AUTH_REGISTER_SUCCESS(state) {
    state.status = "Registered Successfully";
  },
  AUTH_ERROR(state) {
    state.status = "error";
  },
  AUTH_LOGOUT(state) {
    state.status = "logged out";
  },
  SET_USER(state, response) {
    state.user = response.data;
  },
  SET_AUTH_ID(state, response) {
    // make sure of that response.data.id exists before assigning
    state.authenticatedUserId = response && response.data ? response.data.id : null;
  },
  RESTORE_CONTEXT(state, response) {
    // make sure of that response.data exists before assigning
    if (response && response.data) {
      state.user.name = response.data.name;
      state.user.email = response.data.email;
      state.user.roles = response.data.roles;
    } else {
      // If restore fails or no data, clear the context
      state.user = { ...defaultData };
      state.authenticatedUserId = null;
    }
  },
  SET_LOGIN_METHOD(state, response) {
    state.loginMethod = response.data.loginMethod;
  },
  CLEAR_DATA(state) {
    state.user = { ...defaultData }; // Clear user data
    state.authenticatedUserId = null; // Clear authenticated user ID
    state.loginMethod = null; // Clear login method
    state.status = null; // Reset status
    state.lastLoggedInPath = null; // Clear last logged-in path
  },
  // New mutation to set the last logged-in path
  SET_LAST_LOGGED_IN_PATH(state, path) {
    state.lastLoggedInPath = path;
  },
};

// Add a module-level flag to prevent re-entrant/duplicate logout processing
let isProcessingLogout = false;

export const actions = {
  async register({ commit, dispatch }, formData) {
    return axios.post("/api/authentication/register", formData, {
      showToast: true,
      message: "You have successfully registered for AGATRS!",
    });
  },

  async registerPartnerNation({ commit, dispatch }, formData) {
    return axios.post("/api/authentication/register/parnternation", formData, {
      showToast: true,
      message: "You have successfully registered for AGATRS!",
    });
  },

  async login({ commit, dispatch, state }, credentials) {
    try {
      var response = await axios.post("/api/authentication/login", credentials, {
        showToast: true,
        showLoader: true,
        message: "You have successfully logged into AGATRS!",
      });
      commit("SET_USER", response);
      commit("AUTH_LOGIN_SUCCESS"); // Indicate successful login

      const redirectTo = { name: 'Dashboard' };
      
      // Clear lastLoggedInPath if it exists, as it's no longer used for login redirection
      if (state.lastLoggedInPath) {
          commit("SET_LAST_LOGGED_IN_PATH", null); 
      }

      // Navigate to Dashboard if currently on the Login page.
      if (router.currentRoute.name === 'Login' || router.currentRoute.path === '/login') {
        router.push(redirectTo).catch(err => {
          if (err.name !== 'NavigationDuplicated') {
            // Log other navigation errors if they are not NavigationDuplicated
            //console.error('Login action navigation error:', err);
          }
          // If it's NavigationDuplicated, we effectively ignore it
          // as it means the navigation is redundant and doesn't need to be handled,
          // because the user is already at or navigating to the intended destination.
        });
      }
      // If login is called from a non-login page without a redirect query (e.g., session refresh),
      // no explicit navigation is performed here by default. The calling context would handle UI.
    } catch (error) {
      commit("AUTH_ERROR"); // Handle login error state
      throw error; 
    }
  },

  // This is the new action for clearing all logout-related data and redirecting
  async clearAllLogoutData({ commit, state }) {
    if (isProcessingLogout) {
      return;
    }
    isProcessingLogout = true;

    try {
      // Attempt backend logout only if we think a session might exist
      // This is a client-side heuristic; the server is the "single source of truth".
      if (state.user && state.user.name) {
        await axios.get("/api/authentication/logout");
      }
    } catch (error) {
      if (error.response) {
        // If the server responds with 301/302, it often means the user is already logged out
        // and is being redirected (e.g., to a login page). Treat this as a successful logout.
        if (error.response.status === 301 || error.response.status === 302) {
          // axios intercepts
        } else if (error.response.status === 401) {
          // user is logging out.
        } else {
          // For other HTTP errors
          throw error;
        }
      } else {
        // For network errors or other issues where error.response is not set
        throw error;
      }
    } finally {
      commit("CLEAR_DATA"); 
      console.log("Client-side user data cleared.");

      const currentRoute = router.currentRoute.value || router.currentRoute;

      if (router && currentRoute && currentRoute.name !== 'Login') {
        // Navigate to Login page without any redirect query parameter
        router.push({ name: "Login", query: {}, params: { fromLogout: true } }).catch(err => { 
          if (err.name !== 'NavigationDuplicated' && err.name !== 'NavigationCancelled') {
            //console.error('Logout navigation error:', err);
          } else {
            //console.log("Logout navigation: NavigationDuplicated or NavigationCancelled.");
          }
        });
      } else if (router && currentRoute && currentRoute.name === 'Login') {
        // If already on login, but logout is forced, ensure state is clean but don't cause navigation loop
      } else {
        // Fallback if router or currentRoute is somehow undefined
        // Navigate to Login page without any redirect query parameter
        router.push({ name: "Login", query: {}, params: { fromLogout: true } }).catch(err => {
          if (err.name !== 'NavigationDuplicated' && err.name !== 'NavigationCancelled') {
            //console.error('Logout navigation error (fallback):', err);
          }
        });
      }
      isProcessingLogout = false;
    }
  },
  async logout({ dispatch }) {
    //console.log("authentication/logout action called, dispatching clearAllLogoutData.");
    return dispatch("clearAllLogoutData");
  },
  async getUserByEmail({ commit }) {
    commit("SET_AUTH_ID", await axios.get("/api/applicationusers/email"));
  },

  // Used during startup to reload the profile from the server
  async restoreContext({ commit, dispatch }) {
    try {
      const response = await axios.get("/api/authentication/sessioncontext");
      commit("RESTORE_CONTEXT", response);
    } catch (error) {
      commit("CLEAR_DATA"); 
      throw error; // Re-throw to propagate the error for error handling in components
    }
  },
  async clearAuthenticatedUser({commit}){
    commit("SET_AUTH_ID", null);
  },

  async cac_login({ commit, dispatch, state }) {
    try {
      const response = await axios.get("/api/authentication/certificatelogin", {
        showToast: true,
        showLoader: true,
        message: "You have successfully logged into AGATRS!",
      });

      // Commit user data and success status
      commit("SET_USER", response);
      commit("AUTH_LOGIN_SUCCESS");

      const redirectTo = { name: 'Dashboard' };
      const currentRoute = router.currentRoute.value || router.currentRoute;
      
      // Clear lastLoggedInPath if it exists
      if (state.lastLoggedInPath) {
        commit("SET_LAST_LOGGED_IN_PATH", null);
      }

      // Navigate to Dashboard if the the current page is Login.
      if (currentRoute.name === 'Login' || currentRoute.path === '/login') {
        setTimeout(() => {
          router.push(redirectTo).catch(err => {
            if (err.name !== 'NavigationDuplicated' && err.name !== 'NavigationCancelled') {
              console.error('CAC Login navigation error:', err);
            }
          });
        }, 100); // Short delay
      }

    } catch (error) {
      commit("AUTH_ERROR");
      console.error("CAC Login action failed:", error);
      throw error;
    }
  },
  async cac_register({ commit }, user) {
    return axios.get("/api/authentication/certificateregister", {
      showToast: true,
      message: "You have sucsessfully registered your certificate!",
    });
  },
  // This existing clearData action can be consolidated into clearAllLogoutData
  // For now, keeping it here but clearAllLogoutData is more comprehensive for logout scenarios.
  async clearData({ commit }) {
    commit("CLEAR_DATA");
  },
};